import React, { createContext, useContext, useState, useEffect } from 'react';
import { useActiveCustomers } from '../hooks/useActiveCustomers';
import { usePublicOrganizations } from '../hooks/usePublicOrganizations';
import { useAuth0 } from '@auth0/auth0-react';

// Create the context
const CustomersContext = createContext();

/**
 * Provider component for customers data
 * @param {Object} props Component props
 * @returns {JSX.Element} Provider component
 */
export const CustomersProvider = ({ children }) => {
  const { customers, isLoading: isLoadingCustomers, error: customersError } = useActiveCustomers();
  const { organizations, isLoading: isLoadingPublic, error: publicError } = usePublicOrganizations();
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const { user, isAuthenticated } = useAuth0();

  // Combine authenticated customers and public organizations
  const allCustomers = React.useMemo(() => {
    if (isAuthenticated) {
      // If authenticated, use the customers from the API
      return customers;
    } else {
      // If not authenticated, use the public organizations
      return organizations;
    }
  }, [isAuthenticated, customers, organizations]);

  // Set the user's organization as selected when customers are loaded
  useEffect(() => {
    if (allCustomers.length > 0 && isAuthenticated && user?.email) {
      // Try to find the customer matching the user's email
      const userCustomer = allCustomers.find(customer => customer.email === user.email);
      
      if (userCustomer) {
        setSelectedCustomerId(userCustomer.id);
      } else if (!selectedCustomerId) {
        // Fallback: set the first customer as selected if no match is found
        setSelectedCustomerId(allCustomers[0].id);
      }
    } else if (allCustomers.length > 0 && !selectedCustomerId) {
      // For non-authenticated users or if no match found, set the first customer
      setSelectedCustomerId(allCustomers[0].id);
    }
  }, [allCustomers, selectedCustomerId, isAuthenticated, user]);

  // Format customers to match the expected format in the UserSelector component
  const formattedCustomers = React.useMemo(() => {
    return allCustomers.map(customer => ({
      id: customer.id,
      displayName: customer.displayName,
      email: customer.email
    }));
  }, [allCustomers]);

  // Get all customers
  const getAllCustomers = () => formattedCustomers;

  // Get customer by ID
  const getCustomerById = (id) => {
    return formattedCustomers.find(customer => customer.id === id) || null;
  };

  // Check if customer ID is valid
  const isValidCustomer = (id) => {
    return formattedCustomers.some(customer => customer.id === id);
  };

  // Get a customer by shortened ID (handles partial IDs like "Rr2a08tkkXQGa7" for "cus_Rr2a08tkkXQGa7")
  const getCustomerByShortenedId = (shortenedId) => {
    if (!shortenedId) return null;
    
    // First try exact match
    const exactMatch = formattedCustomers.find(customer => customer.id === shortenedId);
    if (exactMatch) return exactMatch;
    
    // Then try to find a customer ID that contains the shortened ID
    return formattedCustomers.find(customer => 
      customer.id && customer.id.includes(shortenedId)
    ) || null;
  };

  // Get the current user's organization
  const getUserOrganization = () => {
    if (!isAuthenticated || !user?.email) return null;
    
    const userCustomer = formattedCustomers.find(customer => customer.email === user.email);
    return userCustomer || null;
  };

  // Determine loading and error states
  const isLoading = isAuthenticated ? isLoadingCustomers : isLoadingPublic;
  const error = isAuthenticated ? customersError : publicError;

  // Context value
  const value = {
    customers: formattedCustomers,
    isLoading,
    error,
    selectedCustomerId,
    setSelectedCustomerId,
    getAllCustomers,
    getCustomerById,
    getCustomerByShortenedId,
    isValidCustomer,
    getUserOrganization
  };

  return (
    <CustomersContext.Provider value={value}>
      {children}
    </CustomersContext.Provider>
  );
};

/**
 * Hook to use the customers context
 * @returns {Object} Customers context
 */
export const useCustomers = () => {
  const context = useContext(CustomersContext);
  if (context === undefined) {
    throw new Error('useCustomers must be used within a CustomersProvider');
  }
  return context;
};
